class Utility {
  static gender = {
    male: "Male",
    female: "Female",
  };
  static getOppositeGender(gender) {
    if (gender === Utility.gender.male) {
      return Utility.gender.female;
    }
    if (gender === Utility.gender.female) {
      return Utility.gender.male;
    }

    return gender;
  }
  static getGenderTypeFromGender(gender, toLocaleLowerCase = true) {
    let gType = "";
    switch (gender) {
      case Utility.gender.male:
        gType = "Movers";
        break;
      case Utility.gender.female:
        gType = "Sitters";
        break;
      default:
        gType = "";
        break;
    }
    if (toLocaleLowerCase) {
      gType = gType.toLocaleLowerCase();
    }
    return gType;
  }
  // Canonical date coercer (GIS-190): accepts a live Firestore Timestamp
  // (.toDate), the serialized {seconds}/{_seconds} shapes callables return,
  // epoch ms, an ISO string, or a Date. Returns null when the value can't be
  // parsed so callers render a dash/nothing instead of "Invalid Date".
  static convertDate(date) {
    if (date == null) return null;
    if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
    if (typeof date.toDate === "function") return date.toDate();
    if (typeof date === "number") return Number.isFinite(date) ? new Date(date) : null;
    if (typeof date === "string") {
      const s = date.trim();
      if (!s) return null;
      // Date-only strings mean a calendar day: parse as LOCAL midnight, not the
      // UTC midnight bare new Date() uses (which renders the previous day in PT).
      const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }
    const seconds = date.seconds ?? date._seconds;
    if (typeof seconds === "number" && !Number.isNaN(seconds)) {
      const nanos = date.nanoseconds ?? date._nanoseconds ?? 0;
      return new Date(seconds * 1000 + nanos / 1000000);
    }
    return null;
  }
  // Shared cents -> "$X,XXX.XX" formatter (was copy-pasted per page).
  static formatUSD(cents) {
    return ((cents || 0) / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }
  static formatDate(date, timeOnly = false) {
    if (!date) return date;

    if (!(date instanceof Date)) date = Utility.convertDate(date);
    if (!date) return "";

    // Days and months arrays for formatting
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Extract date components
    const dayOfWeek = days[date.getDay()];
    const month = months[date.getMonth()];
    const day = date.getDate();

    // Format hours and minutes
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    // Construct formatted date string
    const monthWeekDay = timeOnly ? "" : `${dayOfWeek}, ${month} ${day}`;
    return `${monthWeekDay} ${hours}:${minutes} ${ampm}`;
  }

  static formatDateSessionTime(seconds, includeDayHours = false) {
    const isNegative = seconds < 0;
    const absSeconds = Math.abs(seconds);

    const calculateTimeComponents = (absSeconds) => {
      const days = Math.floor(absSeconds / 86400);
      const remainingSecondsAfterDays = absSeconds % 86400;

      const hours = Math.floor(remainingSecondsAfterDays / 3600);
      const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;

      const minutes = Math.floor(remainingSecondsAfterHours / 60);
      const remainingSeconds = remainingSecondsAfterHours % 60;

      return { days, hours, minutes, remainingSeconds };
    };

    const formatWithLeadingZero = (num) => (num < 10 ? `0${num}` : num);

    const formatDays = (days) => (days > 0 ? `${days}d : ` : "");

    const formatHours = (hours, days, includeDayHours) =>
      days > 0 || includeDayHours ? `${formatWithLeadingZero(hours)}h : ` : "";

    const formatMinutes = (minutes, includeDayHours) =>
      includeDayHours
        ? `${formatWithLeadingZero(minutes)}m : `
        : `${formatWithLeadingZero(minutes)} : `;

    const formatSeconds = (seconds, includeDayHours) =>
      includeDayHours
        ? `${formatWithLeadingZero(seconds)}s`
        : formatWithLeadingZero(seconds);

    const { days, hours, minutes, remainingSeconds } =
      calculateTimeComponents(absSeconds);

    const formattedDays = formatDays(days);
    const formattedHours = formatHours(hours, days, includeDayHours);
    const formattedMinutes = formatMinutes(minutes, includeDayHours);
    const formattedSeconds = formatSeconds(remainingSeconds, includeDayHours);

    const formattedTime = `${formattedDays}${formattedHours}${formattedMinutes}${formattedSeconds}`;

    return `${isNegative ? "-" : ""}${formattedTime.trim()}`;
  }
}
export default Utility;
